import * as React from "react";
import EditFaultComponentCommon from "../../Editing/EditFaultComponentCommon";
import Table from "../../../components/Table/Table";
import { Dimmer } from "semantic-ui-react";
import { editActions } from "../../../actions/editActions";
import { mainActions } from "../../../actions/mainActions";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";
import { setFaultComponentExtraProperties } from "../../../utils/pdApiUtils";
import { debounce, values } from "lodash";

/**
 * All the following props are passed from parent component
 */
export type FaultComponentsTableCommonProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  editedObject?: Api.AnyItem;
  isEditActive: boolean;
  isLoadingFaults: boolean;
  editActions: typeof editActions;
  mainActions: typeof mainActions;
  subMenuRoute: State.EnumerationSubmenu | State.LiftSubmenu;
  parentMenu?: "Lift" | "Enumerations";
  groupName?: Api.GroupName;
  toggleModal: () => void;
  columnFilters: Filter[];
  setColumnFilters: (
    subMenuRoute: State.EnumerationSubmenu | State.LiftSubmenu,
    columnFilter: Filter[]
  ) => void;
};

export const FaultComponentsTableCommon = (props: FaultComponentsTableCommonProps): JSX.Element => {
  const {
    columns,
    pdApi,
    mainState,
    isEditActive,
    toggleModal,
    isLoadingFaults,
    groupName,
    subMenuRoute,
    setColumnFilters,
    editActions,
    editedObjectId,
    editedObject,
    parentMenu
  } = props;

  const { faultComponents } = pdApi;
  const { editSize, pageSize, isShortenTextEnabled } = mainState;

  const handleFilterChangeDebouncedRef = React.useRef(
    debounce((changedFilters: Filter[]) => {
      setColumnFilters(subMenuRoute, changedFilters);
    }, 500)
  );

  React.useEffect(() => {
    handleFilterChangeDebouncedRef.current = debounce((changedFilters: Filter[]) => {
      setColumnFilters(subMenuRoute, changedFilters);
    }, 500);

    return () => {
      props.mainActions.setLastEditedObjectId(undefined);
      handleFilterChangeDebouncedRef.current.cancel();
    };
  }, [props.mainActions, setColumnFilters, subMenuRoute]);

  const rowsAndColumns = React.useMemo(() => {
    let rows: Api.AnyEnumeration[] = [...faultComponents]
      .sort((a: Api.FaultComponentEnumeration, b: Api.FaultComponentEnumeration) => a.enumValue - b.enumValue)
      .map(row => setFaultComponentExtraProperties(row, pdApi));

    let selectedColumns: State.Column[] = columns["Fault Components"];

    const domainGroupId: number | undefined = values(pdApi.domainGroups).find(
      (domainGroup: Api.DomainGroup) => domainGroup.enumName === groupName
    )?.id;

    if (domainGroupId) {
      rows = rows.filter(row => {
        const fc = row as Api.FaultComponentEnumeration;
        return fc.domainGroup?.id === domainGroupId;
      });
      selectedColumns = selectedColumns.filter(column => column.name !== "Domain Group");
    }

    return { rows, selectedColumns };
  }, [columns, faultComponents, groupName, pdApi]);

  const isEnumerationSelected = React.useCallback(
    (enumeration: Api.FaultComponentEnumeration): boolean => {
      const selectedId =
        editedObject && editedObject.id === undefined
          ? undefined
          : editedObjectId ?? mainState.lastEditedObjectId;
      return selectedId !== undefined && enumeration.id === selectedId;
    },
    [editedObject, editedObjectId, mainState.lastEditedObjectId]
  );

  const startingPage = React.useMemo(
    () =>
      mainState.lastEditedObjectId
        ? (rowsAndColumns.rows.findIndex(faultComponent => faultComponent.id === mainState.lastEditedObjectId) /
            mainState.pageSize) >>
          0
        : 0,
    [mainState.lastEditedObjectId, mainState.pageSize, rowsAndColumns.rows]
  );

  return (
    <div className="flex-1">
      <div className="flex-1">
        <Table
          startingPage={startingPage}
          rows={rowsAndColumns.rows}
          loading={isLoadingFaults}
          loadingText={"Fetching fault components..."}
          columns={rowsAndColumns.selectedColumns}
          isHidingTextActive={isShortenTextEnabled}
          handleRowSelection={editActions.select}
          isRowSelected={isEnumerationSelected}
          pageSize={pageSize}
          handleFilterChange={handleFilterChangeDebouncedRef.current}
        />
      </div>
      {isEditActive && (
        <Dimmer active={true} page={true}>
          <div
            className="edit-view"
            role="dialog"
            aria-modal="true"
            aria-label="Edit fault component"
            style={{ flex: editSize, width: editSize === 0.3 ? "500px" : "1000px" }}
          >
            <EditFaultComponentCommon toggleModal={toggleModal} parentMenu={parentMenu} />
          </div>
        </Dimmer>
      )}
    </div>
  );
};

export default FaultComponentsTableCommon;
